/* Genre detail page — `/bookstore/g/:slug`.
 * Shows the books in this genre as a row of flippable cards, plus the
 * bundle offers that include any of these books.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { runTour } from '@/lib/tours';
import { ShieldCheck } from 'lucide-react';
import useCanonicalTags from '@/lib/useCanonicalTags';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Loader2, ShoppingBag, Sparkles } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import StructuredData, { breadcrumbLd } from '@/components/seo/StructuredData';
import { bookstoreApi } from '@/lib/bookstoreApi';
import { useBookstoreAuth } from '@/lib/bookstoreAuthContext';
import SpinCarousel from '@/components/bookstore/SpinCarousel';
import BookCard from '@/components/bookstore/BookCard';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import ProductionNowCard from '@/components/bookstore/ProductionNowCard';
import { Flame } from 'lucide-react';
import { languageFlagMeta } from '@/lib/languageFlags';
// iter-deeplink-gate — A reader can land on /bookstore/g/:slug directly
// (bookmark, shared link, back button) without passing through the
// bookstore landing page's gates first. Re-check here so Mature
// Audiences Only and per-shelf content warnings can never be skipped.
import AdultsOnlyGate, { hasAcceptedAdultsOnly } from '@/components/bookstore/AdultsOnlyGate';
import GenreContentWarningDialog, { hasAcceptedContentWarning } from '@/components/bookstore/GenreContentWarningDialog';

// iter-p3-g — Reading languages shown as filter chips on genre pages
// and as the reader-preference dropdown in the bookstore account.
// Reduce to the primary user-requested set + a couple of common
// extras; the backend supports the full 17-code catalog if you want
// to expose more later.
export const BOOKSTORE_READING_LANGUAGES = [
  { code: '',   label: 'All languages' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'ar', label: 'العربية', rtl: true },
  { code: 'zh', label: '中文' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'ja', label: '日本語' },
];

export default function GenrePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useBookstoreAuth();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(null);
  const [open, setOpen] = useState(null);
  // iter-deeplink-gate — Bump to force a re-render after a gate dialog
  // is accepted (hasAcceptedAdultsOnly / hasAcceptedContentWarning read
  // straight from localStorage, so they aren't reactive on their own).
  const [, setGateTick] = useState(0);
  const bumpGate = () => setGateTick((n) => n + 1);
  // Feb-2026 (indie-conv batch2 pt3) — Deep-link a specific trope from
  // the trending-tropes rail (`/bookstore/g/x?trope=mafia`).
  const initialTropeParam = new URLSearchParams(window.location.search).get('trope');
  // iter-p3-g — Language filter chip state. Preference persists per
  // shelf in localStorage. Also seeds from the reader's account
  // preferred_language so returning shoppers don't have to re-pick.
  // iter-p3-i — On very first visit (no localStorage, no account
  // preference), detect the browser's Accept-Language and pre-check
  // the matching chip if it's non-English AND we support it, so a
  // Spanish speaker never sees the storefront in a language they
  // don't read.
  const [language, setLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem(`bs-shelf-language:${slug}`);
      if (stored !== null) return stored;
      if (user?.preferred_language) return user.preferred_language;
      const nav = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages || [])[0])) || 'en';
      const primary = String(nav).split('-')[0].toLowerCase();
      const known = new Set(BOOKSTORE_READING_LANGUAGES.map((l) => l.code));
      if (primary !== 'en' && known.has(primary)) return primary;
      return '';
    } catch { return ''; }
  });
  // iter-romance28 — Canonical shield lookup for the filter-chip row.
  const canonicalTags = useCanonicalTags();
  // iter-greyrom followup — Reader-driven content-tag filter. Each tag
  // starts in 'neutral' state (not filtering). Clicking cycles it to
  // 'exclude' (hide books with this tag), and clicking again resets it.
  // Selection persists in localStorage per-shelf so a reader who
  // "always hides dubcon" doesn't have to re-configure per visit.
  const [excludedTags, setExcludedTags] = useState(() => {
    try {
      const raw = localStorage.getItem(`bs-shelf-excluded-tags:${slug}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleExclude = (tag) => {
    setExcludedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      try { localStorage.setItem(`bs-shelf-excluded-tags:${slug}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const clearExcludes = () => {
    setExcludedTags(new Set());
    try { localStorage.removeItem(`bs-shelf-excluded-tags:${slug}`); } catch {}
  };

  // Feb-2026 (indie-conv batch2) — Trope-tag filter (include, not exclude)
  // and heat-level upper bound. Persist per-shelf so a reader's preferred
  // reading temperature and favourite tropes stick across sessions.
  const [selectedTropes, setSelectedTropes] = useState(() => {
    try {
      if (initialTropeParam) {
        // Persist the deep-linked trope so a refresh keeps the filter.
        try { localStorage.setItem(`bs-shelf-tropes:${slug}`, JSON.stringify([initialTropeParam])); } catch {}
        return new Set([initialTropeParam]);
      }
      const raw = localStorage.getItem(`bs-shelf-tropes:${slug}`);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleTrope = (t) => {
    bookstoreApi.trackStorefrontEvent({ event_type: 'style_click', genre_slug: slug, style_slug: t, style_label: t });
    setSelectedTropes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t); else next.add(t);
      try { localStorage.setItem(`bs-shelf-tropes:${slug}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  const [heatMax, setHeatMax] = useState(() => {
    try {
      const raw = localStorage.getItem(`bs-shelf-heat-max:${slug}`);
      if (raw !== null) return Number(raw);
      // Feb-2026 (indie-conv batch2 pt3) — Seed from the reader's
      // account-level preferred heat max on first visit (no per-shelf
      // override yet). Falls through to the "show everything" default.
      if (user && typeof user.preferred_heat_max === 'number') {
        return user.preferred_heat_max;
      }
      return 5;
    } catch { return 5; }
  });
  const setHeatMaxPersisted = (n) => {
    setHeatMax(n);
    try { localStorage.setItem(`bs-shelf-heat-max:${slug}`, String(n)); } catch {}
  };

  useSeoMeta(`/bookstore/g/${slug}`, {
    title: `${slug.replace(/-/g, ' ')} — Crown & Rose`,
    description: `Discover ${slug.replace(/-/g, ' ')} novels at the Crown & Rose store.`,
  });

  // iter-p3-k — Remember the last visited shelf so the landing-page
  // language nudge can deep-link back to a personal genre for
  // anonymous readers (logged-in readers use `preferred_genre`).
  useEffect(() => {
    try { localStorage.setItem('bs-last-genre', slug); } catch { /* SSR */ }
  }, [slug]);

  useEffect(() => {
    bookstoreApi.booksByGenreLanguage(slug, language || null)
      .then((d) => { setData(d); setLocked(null); })
      .catch((e) => {
        // iter-50 — 423 Locked = genre disabled or empty. Render a "coming soon"
        // surface so the user understands instead of seeing a generic error.
        if (e?.response?.status === 423) {
          const det = e.response.data?.detail || {};
          setLocked({
            reason: det.reason || 'no_books_yet',
            genre:  det.genre  || { slug, name: slug.replace(/-/g, ' ') },
          });
        } else {
          toast.error(e?.response?.data?.detail || 'Genre not found.');
        }
      });
  }, [slug, language]);

  const pickLanguage = (code) => {
    setLanguage(code);
    try {
      if (code) localStorage.setItem(`bs-shelf-language:${slug}`, code);
      else localStorage.removeItem(`bs-shelf-language:${slug}`);
    } catch { /* SSR */ }
  };

  const startBundle = async (b) => {
    if (!user) {
      navigate(`/bookstore/login?next=/bookstore/g/${slug}`);
      return;
    }
    try {
      const { url } = await bookstoreApi.checkoutBundle(b.id, window.location.origin);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start bundle checkout.');
    }
  };

  const startMaven = async () => {
    if (!user) {
      navigate(`/bookstore/login?next=/bookstore/g/${slug}`);
      return;
    }
    try {
      const { url } = await bookstoreApi.subscribeMaven(window.location.origin, slug);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Maven checkout.');
    }
  };

  // iter-deeplink-gate — A shelf tagged `parent_group: 'adults-only'`
  // needs the outer 18+ checklist first; any `content_warning` shelf
  // (gated or not) needs the per-shelf warning too. Both checks are
  // re-evaluated on every render via the `setGateTick` bump above.
  const genreRow = data?.genre;
  const needsAdultsOnlyGate = genreRow?.parent_group === 'adults-only' && !hasAcceptedAdultsOnly();
  const needsWarnGate = !!genreRow?.content_warning && !hasAcceptedContentWarning(slug);
  const gatePassed = !genreRow || (!needsAdultsOnlyGate && !needsWarnGate);

  // iter-deeplink-gate-checklist — Only fetch the parent's custom
  // checklist when it's actually needed (a deep link to a gated child
  // with the outer gate still unaccepted), so non-gated genre pages
  // never pay for this extra round-trip.
  const [parentChecklist, setParentChecklist] = useState(undefined);
  useEffect(() => {
    if (needsAdultsOnlyGate) {
      bookstoreApi.booksByGenre('adults-only')
        .then((r) => setParentChecklist(r?.genre?.gate_checklist))
        .catch(() => {});
    }
  }, [needsAdultsOnlyGate]);

  return (
    <div className="min-h-screen bg-background text-foreground pb-[max(6rem,env(safe-area-inset-bottom))]" data-testid={`bs-genre-page-${slug}`}>
      <AdultsOnlyGate
        open={needsAdultsOnlyGate}
        checklist={parentChecklist}
        onAccept={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <GenreContentWarningDialog
        genre={(!needsAdultsOnlyGate && needsWarnGate) ? genreRow : null}
        onAcceptOnce={bumpGate}
        onRetreat={() => navigate('/bookstore')}
      />
      <StructuredData
        id="breadcrumb"
        data={breadcrumbLd([
          { name: 'Crown & Rose', url: typeof window !== 'undefined' ? window.location.origin : '' },
          { name: 'Book Store', url: typeof window !== 'undefined' ? `${window.location.origin}/bookstore` : '' },
          { name: slug.replace(/-/g, ' '), url: typeof window !== 'undefined' ? `${window.location.origin}/bookstore/g/${slug}` : '' },
        ])}
      />
      <header className="sticky top-0 z-40 border-b border-border/60 bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/bookstore" data-testid="bs-genre-back"
                className="inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground">
            <ArrowLeft className="h-3 w-3" /> All genres
          </Link>
          <div className="flex items-center gap-2 text-[12px]">
            <Link to="/" className="rounded-sm px-2.5 py-1 text-muted-foreground hover:bg-secondary hover:text-foreground">Home</Link>
            {user ? (
              <Link to="/bookstore/account" className="rounded-sm bg-primary px-3 py-1 font-medium text-primary-foreground">My shelf</Link>
            ) : (
              <Link to="/bookstore/login" className="rounded-sm bg-primary px-3 py-1 font-medium text-primary-foreground">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 py-12">
        {/* Dark Romance vault banner — full-width, shown for the dark-romance slug only.
            Locked: the vault appears sealed with pulsing glow.
            Unlocked: reveals the moody header with a subtle unlock effect. */}
        {/* Sci-Fi shelf banner */}
        {slug === 'sci-fi' && (
          <div
            data-testid="bs-genre-sci-fi-banner"
            className="relative mb-10 overflow-hidden rounded-2xl"
            style={{
              boxShadow: '0 0 90px rgba(10,20,60,0.70), inset 0 1px 0 rgba(80,180,220,0.08)',
              border:    '1px solid rgba(30,80,140,0.50)',
              minHeight: '340px',
            }}
          >
            {/* Cinematic background */}
            <img
              src="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/d9a7ca6d89baab9ff8afd4736e22f12feeecc54e7c1d322652c524d2e980c52c.jpeg"
              alt="Science Fiction"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center 40%' }}
            />

            {/* Left-to-right gradient so text sits on darkness */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to right, rgba(2,6,24,0.92) 0%, rgba(2,6,24,0.58) 52%, rgba(2,6,24,0.20) 100%)',
              }}
            />

            {/* Subtle teal scan-line glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{ boxShadow: 'inset 0 0 120px rgba(0,180,220,0.07)' }}
            />

            <div
              className="relative z-10 flex flex-col items-start justify-end px-10 py-12 h-full"
              style={{ minHeight: '340px' }}
            >
              {/* Eyebrow badge */}
              <span
                className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-0.5
                           text-[10px] uppercase tracking-[0.25em]"
                style={{
                  borderColor: 'rgba(0,200,240,0.30)',
                  background:  'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(4px)',
                  color: '#5cd8f0',
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
                </svg>
                Science Fiction Shelf
              </span>

              {/* Main title */}
              <h1
                className="font-serif text-5xl font-bold tracking-tight text-white md:text-6xl"
                style={{ textShadow: '0 2px 40px rgba(0,40,80,0.95), 0 0 80px rgba(0,180,220,0.25)' }}
              >
                Science Fiction
              </h1>

              {/* Accent line */}
              <div
                className="mt-2 h-px w-20 rounded-full"
                style={{ background: 'linear-gradient(90deg, #3cc8e8, transparent)' }}
              />

              {/* Tagline */}
              <p className="mt-4 max-w-md text-[13.5px] leading-relaxed"
                 style={{ color: 'rgba(160,220,240,0.82)' }}>
                Between the dying stars and the rain-slick streets —
                the only literature that reads the future's receipt.
              </p>

              {/* Sub-tags */}
              <div
                className="mt-5 flex items-center gap-2 text-[11px]"
                style={{ color: 'rgba(100,200,230,0.65)' }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                  style={{ background: '#3cc8e8' }}
                />
                First contact · Memory economies · Generation ships
              </div>
            </div>
          </div>
        )}

        {/* Gothic Horror shelf banner */}
        {slug === 'gothic-horror' && (
          <div
            data-testid="bs-genre-gothic-horror-banner"
            className="relative mb-10 overflow-hidden rounded-2xl"
            style={{
              boxShadow: '0 0 90px rgba(14,38,14,0.65), inset 0 1px 0 rgba(140,180,120,0.08)',
              border:    '1px solid rgba(45,75,45,0.55)',
              minHeight: '340px',
            }}
          >
            {/* Cinematic background */}
            <img
              src="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/f511ae47bcd89b097e01eee422d3469c727b447c7554aaa9f5518bedd0e65b1a.jpeg"
              alt="Gothic Horror"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: 'center 30%' }}
            />

            {/* Deep gradient overlay — left-to-right fade so text sits on darkness */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to right, rgba(4,12,4,0.92) 0%, rgba(4,12,4,0.60) 55%, rgba(4,12,4,0.30) 100%)',
              }}
            />

            {/* Subtle green vignette glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl"
              style={{ boxShadow: 'inset 0 0 100px rgba(30,80,30,0.12)' }}
            />

            <div
              className="relative z-10 flex flex-col items-start justify-end px-10 py-12 h-full"
              style={{ minHeight: '340px' }}
            >
              {/* Eyebrow badge */}
              <span
                className="mb-4 inline-flex items-center gap-2 rounded-full border px-3 py-0.5
                           text-[10px] uppercase tracking-[0.25em]"
                style={{
                  borderColor: 'rgba(120,180,100,0.35)',
                  background: 'rgba(0,0,0,0.45)',
                  backdropFilter: 'blur(4px)',
                  color: '#a8d08a',
                }}
              >
                {/* Raven feather icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2z"/>
                  <path d="M12 8v8M8 12l4-4 4 4"/>
                </svg>
                Gothic Horror Shelf
              </span>

              {/* Main title */}
              <h1
                className="font-serif text-5xl font-bold tracking-tight text-white md:text-6xl"
                style={{ textShadow: '0 2px 40px rgba(4,12,4,0.9), 0 0 80px rgba(30,80,30,0.3)' }}
              >
                Gothic Horror
              </h1>

              {/* Accent line */}
              <div
                className="mt-2 h-px w-20 rounded-full"
                style={{ background: 'linear-gradient(90deg, #7ab860, transparent)' }}
              />

              {/* Tagline */}
              <p className="mt-4 max-w-md text-[13.5px] leading-relaxed"
                 style={{ color: 'rgba(200,230,185,0.80)' }}>
                Every locked room has a reason. Every old house has been waiting —
                specifically, for someone like you.
              </p>

              {/* Book count */}
              <div
                className="mt-5 flex items-center gap-2 text-[11px]"
                style={{ color: 'rgba(160,210,140,0.70)' }}
              >
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
                  style={{ background: '#7ab860' }}
                />
                Ancestral curses · Haunted houses · Forbidden rituals
              </div>
            </div>
          </div>
        )}

        {slug === 'dark-romance' && (
          <div
            data-testid="bs-genre-dark-romance-vault-banner"
            className="relative mb-10 overflow-hidden rounded-2xl"
            style={{
              boxShadow: '0 0 80px rgba(90,28,31,0.55), inset 0 1px 0 rgba(198,166,100,0.10)',
              border:    '1px solid rgba(90,28,31,0.55)',
              minHeight: '340px',
            }}
          >
            {/* Photorealistic background image */}
            <img
              src="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/e15ab3d5d9c2cf06862de130249d8716104ce8dd161c8e54b7021146ed30efde.jpeg"
              alt="Dark Romance"
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={{ objectPosition: 'center 20%' }}
            />

            {/* Gradient overlay — deeper when locked, lighter when unlocked */}
            <div
              className="absolute inset-0"
              style={{
                background: gatePassed
                  ? 'linear-gradient(to right, rgba(10,4,6,0.82) 0%, rgba(10,4,6,0.50) 55%, rgba(10,4,6,0.72) 100%)'
                  : 'linear-gradient(to right, rgba(10,4,6,0.92) 0%, rgba(10,4,6,0.72) 55%, rgba(10,4,6,0.88) 100%)',
              }}
            />

            {/* Gold vignette edge glow */}
            <div className="pointer-events-none absolute inset-0 rounded-2xl"
                 style={{ boxShadow: 'inset 0 0 80px rgba(198,166,100,0.08)' }} />

            <div className="relative z-10 flex flex-col items-start justify-end px-10 py-12 h-full" style={{ minHeight: '340px' }}>
              {/* Eyebrow */}
              <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#c6a664]/40 bg-black/40 backdrop-blur-sm px-3 py-0.5 text-[10px] uppercase tracking-[0.25em] text-[#c6a664]">
                {gatePassed ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0"/></svg>
                )}
                {gatePassed ? 'Vault Unlocked' : '18 + Content Vault'}
              </span>

              {/* Main title */}
              <h1 className="font-serif text-5xl font-bold tracking-tight text-white md:text-6xl"
                  style={{ textShadow: '0 2px 32px rgba(10,4,6,0.8)' }}>
                Dark Romance
              </h1>
              <div className="mt-2 h-px w-20 rounded-full"
                   style={{ background: 'linear-gradient(90deg, #c6a664, transparent)' }} />

              {/* Tagline — reflects the story's arc */}
              <p className="mt-4 max-w-md text-[13.5px] leading-relaxed text-[#e8c7cc]/80">
                {gatePassed
                  ? 'Power. Obsession. The moment her defiance becomes his undoing — and hers.'
                  : 'Control turning into love. A woman who refuses to bend — until she chooses to. Adults only.'}
              </p>

              {/* Lock state indicator */}
              {!gatePassed && (
                <div className="mt-5 flex items-center gap-2 text-[11px] text-[#c6a664]/80">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c6a664] animate-pulse" />
                  Confirm your age to unlock
                </div>
              )}
            </div>
          </div>
        )}

        {!gatePassed ? (
          /* iter-deeplink-gate — Dialogs above are open; hold the real
             shelf content back until the reader clicks through. */
          <div className="flex h-72 items-center justify-center text-muted-foreground" data-testid={`bs-genre-gate-pending-${slug}`}>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking access…
          </div>
        ) : locked ? (
          /* iter-50 — Locked genre surface: tile shows "Coming soon" instead of an error. */
          <div className="mx-auto max-w-xl rounded-xl border border-amber-300/60 bg-amber-50/40 p-8 text-center" data-testid="bs-genre-locked">
            <div className="mb-2 text-[10.5px] uppercase tracking-[0.22em] text-amber-700">Coming soon</div>
            <h1 className="text-3xl font-semibold tracking-tight text-amber-900">{locked.genre.name}</h1>
            <p className="mt-3 text-[13.5px] text-amber-800/85">
              {locked.reason === 'disabled_by_admin'
                ? 'This shelf is temporarily curated by our editors. Check back soon — new titles arrive every week.'
                : "We haven't stocked this shelf yet. Every Tuesday a new wave of finished novels lands."}
            </p>
            <Link to="/bookstore" className="mt-6 inline-flex items-center gap-1 rounded-sm bg-amber-700/90 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-amber-800">
              <ArrowLeft className="h-3 w-3" /> Browse other shelves
            </Link>
          </div>
        ) : !data ? (
          <div className="flex h-72 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading {slug}…
          </div>
        ) : (
          <>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border/70 bg-card px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <BookOpen className="h-3 w-3" /> {data.genre.name}
            </div>
            <h1 className="text-4xl font-semibold tracking-tight">{data.genre.name}</h1>
            <p className="mt-2 max-w-2xl text-[13.5px] text-muted-foreground">{data.genre.blurb}</p>

            {/* iter-greyrom — Genre manifesto. Rendered for shelves that
                ship a `manifesto` field on the genre row (Dark Romance,
                Grey Romance). Sets the tone before the reader browses. */}
            {data.genre.manifesto && (
              <div
                data-testid={`bs-genre-manifesto-${slug}`}
                className="mt-6 max-w-2xl rounded-lg border border-oxblood/30 bg-oxblood/[0.06] px-5 py-4 text-[13.5px] leading-relaxed text-foreground/85"
              >
                <div className="mb-1 text-[10px] uppercase tracking-[0.22em] text-oxblood dark:text-red-300">
                  The shelf, in one breath
                </div>
                {data.genre.manifesto}
              </div>
            )}

            <div className="mt-8">
              <h2 className="mb-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Books in this shelf</h2>

              {/* iter-p3-g — Language pill row.  One-tap jump to any
                  supported language.  Persists per-shelf, seeds from
                  the reader's `preferred_language` on first visit. */}
              <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid={`bs-genre-language-chips-${slug}`}>
                <span className="mr-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Language:</span>
                {BOOKSTORE_READING_LANGUAGES.map((l) => {
                  const active = (language || '') === l.code;
                  const flag = languageFlagMeta(l.code);
                  return (
                    <button
                      key={l.code || 'any'}
                      type="button"
                      onClick={() => pickLanguage(l.code)}
                      data-testid={`bs-genre-language-chip-${l.code || 'all'}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[10.5px] uppercase tracking-wider transition ${
                        active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-secondary/50 text-muted-foreground hover:border-primary/60 hover:text-foreground'
                      }`}
                      dir={l.rtl ? 'rtl' : undefined}
                    >
                      {flag && <img src={flag.flagUrl} alt="" className="h-3 w-3 rounded-[1px] object-cover" />}
                      {l.label}
                    </button>
                  );
                })}
              </div>

              {/* iter-greyrom followup — Content-tag filter chips. Union
                  of every tag across books in this shelf. Click a chip
                  to hide titles carrying that tag; hidden count shown
                  under the chip row. */}
              {(() => {
                const tagCounts = {};
                (data.books || []).forEach((b) => {
                  (b.content_tags || []).forEach((t) => {
                    tagCounts[t] = (tagCounts[t] || 0) + 1;
                  });
                });
                const tagOptions = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
                if (tagOptions.length === 0) return null;
                // iter-romance25 — First-time explainer on the Dark
                // Romance shelf (where the chips are most likely to be
                // encountered first). No-op elsewhere.
                if (slug === 'dark-romance') runTour('bookstore-tag-filters-open');
                const filteredCount = (data.books || []).filter(
                  (b) => (b.content_tags || []).some((t) => excludedTags.has(t)),
                ).length;
                return (
                  <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid={`bs-genre-tag-filters-${slug}`}>
                    <span className="mr-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Hide titles with:</span>
                    {tagOptions.map(([t, count]) => {
                      const excluded = excludedTags.has(t);
                      const canon = canonicalTags.isCanonical(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleExclude(t)}
                          data-testid={`bs-genre-tag-chip-${t.replace(/\s+/g, '-').toLowerCase()}`}
                          title={canon ? 'Canonical shelf-wide label' : ''}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-[2px] text-[10.5px] uppercase tracking-wider transition ${
                            excluded
                              ? 'border-oxblood bg-oxblood text-white line-through decoration-[1.5px]'
                              : 'border-border bg-secondary/50 text-muted-foreground hover:border-oxblood/60 hover:text-foreground'
                          }`}
                        >
                          {canon && <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />}
                          {t} <span className="opacity-70">·{count}</span>
                        </button>
                      );
                    })}
                    {excludedTags.size > 0 && (
                      <button
                        type="button"
                        onClick={clearExcludes}
                        data-testid={`bs-genre-tag-clear-${slug}`}
                        className="ml-1 rounded-full border border-border px-2 py-[2px] text-[10.5px] uppercase tracking-wider text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      >
                        Reset ({filteredCount} hidden)
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Feb-2026 (indie-conv batch2) — Trope + heat filters. */}
              {(() => {
                const tropeCounts = {};
                (data.books || []).forEach((b) => {
                  (b.tropes || []).forEach((t) => {
                    tropeCounts[t] = (tropeCounts[t] || 0) + 1;
                  });
                });
                const tropeOptions = Object.entries(tropeCounts).sort((a, b) => b[1] - a[1]).slice(0, 24);
                const hasHeat = (data.books || []).some((b) => Number(b.heat_level || 0) > 0);
                if (tropeOptions.length === 0 && !hasHeat) return null;
                return (
                  <>
                    {tropeOptions.length > 0 && (
                      <div className="mb-3 flex flex-wrap items-center gap-1.5"
                           data-testid={`bs-genre-trope-filters-${slug}`}>
                        <span className="mr-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Tropes:</span>
                        {tropeOptions.map(([t, count]) => {
                          const on = selectedTropes.has(t);
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => toggleTrope(t)}
                              aria-pressed={on}
                              data-testid={`bs-genre-trope-chip-${t}`}
                              className={`rounded-full border px-2 py-[2px] text-[10.5px] uppercase tracking-wider transition ${
                                on
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border bg-secondary/50 text-muted-foreground hover:border-primary/60 hover:text-foreground'
                              }`}
                            >
                              {t} <span className="opacity-70">·{count}</span>
                            </button>
                          );
                        })}
                        {selectedTropes.size > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedTropes(new Set());
                              try { localStorage.removeItem(`bs-shelf-tropes:${slug}`); } catch {}
                            }}
                            data-testid={`bs-genre-trope-clear-${slug}`}
                            className="ml-1 rounded-full border border-border px-2 py-[2px] text-[10.5px] uppercase tracking-wider text-muted-foreground hover:border-primary/50 hover:text-foreground"
                          >
                            Clear tropes
                          </button>
                        )}
                      </div>
                    )}
                    {hasHeat && (
                      <div className="mb-4 flex items-center gap-2"
                           data-testid={`bs-genre-heat-filter-${slug}`}>
                        <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Max heat:</span>
                        <input
                          type="range"
                          min={0}
                          max={5}
                          step={1}
                          value={heatMax}
                          onChange={(e) => setHeatMaxPersisted(parseInt(e.target.value, 10))}
                          data-testid={`bs-genre-heat-slider-${slug}`}
                          className="w-40 accent-orange-500"
                          aria-label="Maximum heat level"
                        />
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-500/20 px-2 py-0.5 text-[10.5px] font-semibold text-orange-700 dark:text-orange-300"
                              data-testid={`bs-genre-heat-value-${slug}`}>
                          {heatMax === 0
                            ? 'Sweet only'
                            : (
                              <>
                                {Array.from({ length: heatMax }).map((_, i) => (
                                  <Flame key={i} className="h-3 w-3" fill="currentColor" strokeWidth={2} />
                                ))}
                                <span className="ml-0.5">/ 5</span>
                              </>
                            )}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}


              <SpinCarousel testid={`bs-genre-books-${slug}`}>
                {!language && data.books.length === 0 && (
                  <ProductionNowCard shelf={data.genre.name} testId={`books-in-production-${slug}`} />
                )}
                {data.books
                  .filter((b) => !(b.content_tags || []).some((t) => excludedTags.has(t)))
                  .filter((b) => Number(b.heat_level || 0) <= heatMax)
                  .filter((b) => selectedTropes.size === 0
                    || (b.tropes || []).some((t) => selectedTropes.has(t)))
                  .map((b) => (
                    <BookCard
                      key={b.id}
                      book={b}
                      onClick={setOpen}
                      onLanguageClick={(code) => pickLanguage(String(code).split('-')[0].toLowerCase())}
                    />
                  ))}
              </SpinCarousel>
              {/* iter-p3-h — Friendly empty state when the language
                  filter yields zero books.  One-tap reset back to
                  All so the reader is never stuck staring at an
                  empty carousel. */}
              {language && (data.books || []).length === 0 && (
                <div
                  className="mt-4 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-6 text-center"
                  data-testid={`bs-genre-language-empty-${slug}`}
                >
                  <div className="mb-1 text-[11px] uppercase tracking-[0.16em] text-primary/80">
                    Nothing here yet
                  </div>
                  <div className="text-[14px] font-semibold tracking-tight">
                    No {BOOKSTORE_READING_LANGUAGES.find((l) => l.code === language)?.label || language} titles in {data.genre.name} yet.
                  </div>
                  <p className="mt-1 text-[12px] text-muted-foreground">
                    We're stocking translations weekly. In the meantime, browse the whole shelf.
                  </p>
                  <button
                    type="button"
                    onClick={() => pickLanguage('')}
                    data-testid={`bs-genre-language-reset-${slug}`}
                    className="mt-4 inline-flex items-center gap-1 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    Show all languages
                  </button>
                </div>
              )}
              {excludedTags.size > 0 && (
                <p className="mt-2 text-[10.5px] italic text-muted-foreground" data-testid={`bs-genre-hidden-note-${slug}`}>
                  Filtering active — {[...excludedTags].join(', ')} hidden. Preferences saved to this browser.
                </p>
              )}
            </div>

            {data.bundles.length > 0 && (
              <div className="mt-10">
                <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Bundle deals</h2>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {data.bundles.map((bd) => (
                    <button key={bd.id} onClick={() => startBundle(bd)}
                            data-testid={`bs-genre-bundle-${bd.id}`}
                            className="relative overflow-hidden rounded-xl border border-border bg-card p-5 text-left transition hover:border-primary/50 hover:shadow-panel-active">
                      {/* iter-greyrom followup — "New addition" ribbon
                          for bundles that were recently refreshed.
                          Rotated top-right so returning shoppers spot
                          the change without reading the blurb. iter-
                          romance25 — Ribbon quietly retires once
                          `new_addition_expires_at` has passed. */}
                      {bd.new_addition_note && !(bd.new_addition_expires_at && new Date(bd.new_addition_expires_at) < new Date()) && (
                        <div
                          data-testid={`bs-bundle-new-ribbon-${bd.id}`}
                          className="pointer-events-none absolute -right-10 top-3 rotate-45 bg-oxblood px-10 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.18em] text-white shadow-md"
                        >
                          {bd.new_addition_note}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {bd.book_covers?.some(Boolean) && (
                            <div className="flex shrink-0 -space-x-3">
                              {bd.book_covers.filter(Boolean).slice(0, 3).map((url, i) => (
                                <img
                                  key={i}
                                  src={url}
                                  alt=""
                                  className="h-14 w-10 rounded-sm border border-border object-cover shadow-sm"
                                  style={{ zIndex: 10 - i }}
                                />
                              ))}
                            </div>
                          )}
                          <div>
                            <div className="text-[15px] font-semibold tracking-tight">{bd.title}</div>
                            <div className="text-[12px] text-muted-foreground">{bd.blurb}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-semibold text-primary">${(bd.bundle_price_cents / 100).toFixed(2)}</div>
                          <div className="text-[11px] text-muted-foreground line-through">${(bd.regular_price_cents / 100).toFixed(2)}</div>
                          <div className="text-[10.5px] font-semibold text-success">{bd.savings_label}</div>
                        </div>
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                        <ShoppingBag className="h-3.5 w-3.5" /> Get the bundle
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 rounded-xl border border-primary/40 bg-gradient-to-br from-primary/15 via-card to-card p-6">
              <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-[1fr_auto]">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-primary">Weekly Maven</div>
                  <div className="mt-1 text-xl font-semibold tracking-tight">Pick a new {data.genre.name.toLowerCase()} book every week.</div>
                  <p className="mt-1 text-[12px] text-muted-foreground">Subscribers pick any unread title in their genre, weekly.</p>
                </div>
                <button onClick={startMaven} data-testid="bs-maven-subscribe"
                        className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90">
                  <Sparkles className="h-4 w-4" /> Become a Weekly Maven
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {open && <BookDetailModal book={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
